import { isNil } from 'lodash'

import { getTokenCookies } from 'features/user'
import { userFromDTO } from 'features/user/api/mappers/user'
import { REFRESH_TOKEN } from 'features/user/api/mutations/refreshToken'
import { RefreshTokenData, RefreshTokenVars } from 'features/user/api/types/refreshToken'
import { authenticate, reset } from 'features/user/store/userSlice'
import { store } from 'providers/redux'
import { request } from 'providers/tanstack-query/client'
import { GraphQlResponse, handleResponse200 } from 'providers/tanstack-query/response'
import { getKey } from 'providers/tanstack-query/utils'
import { removeTokenCookies } from './tokenCookies'
import { RefreshTokenResult } from './types'

/**
 * Remember to catch Promise errors when using this function because it does not handle them internally.
 */
export const refreshToken = (): Promise<RefreshTokenResult> => {
  const { refreshToken } = getTokenCookies()

  if (isNil(refreshToken)) {
    store.dispatch(reset())

    return Promise.resolve(undefined)
  }

  return request<RefreshTokenVars, RefreshTokenData>({
    key: getKey(REFRESH_TOKEN),
    query: REFRESH_TOKEN,
    variables: { refreshToken },
  }).then((response: GraphQlResponse<RefreshTokenData> | void) => {
    const data: RefreshTokenData | undefined = handleResponse200<RefreshTokenData>({ response })

    if (data === undefined) {
      return undefined
    }

    const {
      refreshToken: { accessToken, errors, refreshToken, user },
    } = data

    const hasErrors: boolean = !isNil(errors) && errors.length > 0

    if (isNil(accessToken) || isNil(refreshToken) || isNil(user) || hasErrors) {
      removeTokenCookies()
      window.location.reload()
      return undefined
    }

    store.dispatch(
      authenticate({
        accessToken,
        refreshToken,
        user: userFromDTO(user),
      }),
    )

    return { accessToken, refreshToken, user }
  })
}
